import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sl1ImportReport } from "shared/sl1Import"
import { Sl1ImportCounts } from "shared/sl1ImportData"
import { eyebrowSx, fmtNum, panelSx } from "./Sl1ImportChrome"

/**
 * The resolution bar + its ledger (LOCKER-382 §D-4.3).
 *
 * Two views of one thing. The BAR is the shape of the run at a glance — where a
 * provider's rows landed. The LEDGER underneath is the same six numbers with the
 * counter, an independently-computed value and the delta between them, which is
 * what makes the bar checkable rather than merely decorative.
 *
 * ## Why the ledger exists at all when every delta is structurally zero
 *
 * `counts` is denormalised onto the job Thing on purpose — the job list renders
 * with zero report reads and survives a report being deleted. The price of that
 * is a second copy of six numbers, and the failure it invites is the copy drifting
 * from the report it summarises. A non-zero delta here is that drift, shown rather
 * than left for a reader to trust. A ledger of zeroes is the assertion passing.
 */

/**
 * Fields are `metric` / `label`, NOT `key` / `labelKey`.
 *
 * Gitleaks' `generic-api-key` rule fires on `key: "<10+ char string>"`, which is
 * exactly the shape of an i18n descriptor array — and the allowlist for it cannot
 * be made to work on the version CI pins (see `.gitleaks.toml`). Renaming the
 * field removes the false positive at the source, costs nothing, and is a better
 * name anyway: one is a counts metric, the other a translation label.
 */
const SEGMENTS: {
  metric: keyof Sl1ImportCounts
  label: string
  color: string
}[] = [
  { metric: "matchedExact", label: "sl1SegMatchedExact", color: "success.main" },
  { metric: "matchedFallback", label: "sl1SegMatchedFallback", color: "success.light" },
  { metric: "unmatched", label: "sl1SegUnmatched", color: "primary.main" },
  { metric: "review", label: "sl1SegReview", color: "info.main" },
  { metric: "blocked", label: "sl1SegBlocked", color: "error.main" },
]

export const Sl1ResolutionBar: FC<{ report: Sl1ImportReport }> = ({ report }) => {
  const { t } = useTranslation()
  const counts = report.counts
  // `review` overlaps the resolution buckets (a reviewable row is still unmatched
  // or matched), so the bar is drawn over `found` and the review segment is shown
  // as a marker below rather than as a slice competing for the same width.
  //
  // `replacesHusk` is likewise NOT drawn: it is a subset of the matched segments,
  // and a slice for it would double-count the same rows and make the bar wider
  // than the run. It is read in the ledger and on the report page's tiles.
  const total = Math.max(1, counts.found)
  const drawn = SEGMENTS.filter((s) => s.metric !== "review")

  return (
    <Box data-ui="UI-SL2W-176" sx={{ ...panelSx, p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography sx={eyebrowSx}>{t("sl1ResolutionBar")}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("sl1ResolutionOf", { n: fmtNum(counts.found) })}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        sx={{ height: 14, borderRadius: 1, overflow: "hidden", bgcolor: "action.hover" }}
      >
        {drawn.map((s) => {
          const v = counts[s.metric] ?? 0
          if (!v) return null
          return (
            <Tooltip key={s.metric} title={`${t(s.label)}: ${fmtNum(v)}`}>
              <Box sx={{ width: `${(v / total) * 100}%`, bgcolor: s.color }} />
            </Tooltip>
          )
        })}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={0.5} sx={{ mt: 1.5 }}>
        {SEGMENTS.map((s) => (
          <Stack key={s.metric} direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: s.metric === "review" ? "50%" : 0.5,
                bgcolor: s.color,
              }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t(s.label)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {fmtNum(counts[s.metric] ?? 0)}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Table size="small" sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={eyebrowSx}>{t("sl1LedgerMetric")}</TableCell>
            <TableCell sx={eyebrowSx} align="right">
              {t("sl1LedgerCounter")}
            </TableCell>
            <TableCell sx={eyebrowSx} align="right">
              {t("sl1LedgerComputed")}
            </TableCell>
            <TableCell sx={eyebrowSx} align="right">
              {t("sl1LedgerDelta")}
            </TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1LedgerFrom")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.resolution.map((line) => (
            <TableRow key={line.metric}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.74rem" }}>
                {line.metric}
                {!line.exact && (
                  // A lower bound is not a count, and a row that does not say so
                  // reads as one.
                  <Tooltip title={t("sl1LedgerInexactHint")}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.75, color: "info.main", fontWeight: 700 }}
                    >
                      {t("sl1LedgerLowerBound")}
                    </Typography>
                  </Tooltip>
                )}
                {line.note && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.disabled", maxWidth: "62ch" }}
                  >
                    {line.note}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {fmtNum(line.counter)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {fmtNum(line.computed)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: line.delta ? 700 : 400,
                  color: line.delta ? "error.main" : "text.disabled",
                }}
              >
                {line.delta === null ? "—" : line.delta === 0 ? "0" : `${line.delta > 0 ? "+" : ""}${line.delta}`}
              </TableCell>
              <TableCell sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                {line.computedFrom}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default Sl1ResolutionBar
